import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useChatbots } from "../contexts/ChatbotContext";
import { useAuth } from "../contexts/AuthContext";
import config from "../config";
import "./DirectoryPage.css";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

// Generate a consistent pastel color based on the tag string
const getTagColor = (tag) => {
  // Simple hash function to get a number from a string
  const hash = tag.split("").reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);

  const h = Math.abs(hash) % 360;
  
  // Check if dark mode is active
  const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
  
  if (isDarkMode) {
    // Dark mode: darker background with brighter text
    return `hsl(${h}, 45%, 30%)`; // Darker, more saturated background
  } else {
    // Light mode: pastel colors
    return `hsl(${h}, 70%, 80%)`;
  }
};

const DirectoryPage = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const directoryId = query.get("id");
  const { getChatbotById, refreshChatbots } = useChatbots();
  const { user, isCheckingAuth } = useAuth();

  const [directoryTitle, setDirectoryTitle] = useState("Loading directory...");
  const [directoryData, setDirectoryData] = useState(null);
  const [childItems, setChildItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to check if a string is an SVG
  const isSvgContent = (str) => {
    if (!str) return false;
    return typeof str === "string" && str.trim().startsWith("<");
  };

  // We need to ensure we have the latest data from the chatbots context
  useEffect(() => {
    // Only refresh on initial mount
    refreshChatbots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once on mount

  useEffect(() => {
    if (directoryId) {
      // Fetch the directory details (the parent chatbot)
      const fetchDirectoryData = async () => {
        try {
          // Get the directory info using the existing context function
          const directoryData = getChatbotById(directoryId);

          if (directoryData) {
            setDirectoryTitle(
              directoryData.title || directoryData.name || "Untitled Directory"
            );
            setDirectoryData(directoryData);
            setError(null);
          } else {
            setError("Directory not found.");
            setDirectoryTitle("Error");
          }
        } catch (err) {
          setError("Failed to load directory data.");
          setDirectoryTitle("Error");
          console.error("Error fetching directory data:", err);
        }
      };

      // Fetch all items (both directories and chatbots) using our API endpoint
      const fetchChildItems = async () => {
        try {
          const response = await fetch(
            `${config.api.baseUrl}/chatbot/by-parent/${directoryId}`,
            {
              method: "GET",
              credentials: "include",
            }
          );

          const data = await response.json();

          if (data.success) {
            console.log(
              `Fetched ${data.chatbots.length} items from directory ${directoryId}`
            );
            console.log("Items data:", data.chatbots);

            // Debug the types of items we're getting
            const directories = data.chatbots.filter(
              (item) => item.directory === true
            );
            const chatbots = data.chatbots.filter(
              (item) => item.directory === false
            );
            console.log(
              `Directories: ${directories.length}, Chatbots: ${chatbots.length}`
            );

            // Log sample from each category
            if (directories.length > 0)
              console.log("Sample directory:", directories[0]);
            if (chatbots.length > 0)
              console.log("Sample chatbot:", chatbots[0]);

            const items = data.chatbots || [];
            const hasAccess = (item) => {
              if (
                !item ||
                !Array.isArray(item.access) ||
                item.access.length === 0
              )
                return false;
              // Guests must be explicitly listed by email or email prefix (username)
              if (user?.role === "Guest") {
                if (!user?.email) return false;
                const prefix = user.email.split("@")[0];
                return (
                  item.access.includes(user.email) ||
                  item.access.includes(prefix)
                );
              }
              // Non-guests: "All" grants access
              if (item.access.includes("All")) return true;
              // Top Manager full access
              if (user?.role === "Top Manager") return true;
              // Email or role match
              if (user?.email && item.access.includes(user.email)) return true;
              if (user?.role && item.access.includes(user.role)) return true;
              // Username prefix match (non-email, non-role entries)
              if (user?.email) {
                const prefix = user.email.split("@")[0];
                const nonRoleAcc = item.access.filter(
                  (acc) =>
                    acc !== "Admin" &&
                    acc !== "Super Admin" &&
                    acc !== "All" &&
                    !acc.includes("@")
                );
                if (nonRoleAcc.includes(prefix)) return true;
              }
              return false;
            };
            const filtered = items.filter(hasAccess);
            setChildItems(filtered);
          } else {
            console.error("Failed to fetch child items:", data.error);
          }
        } catch (err) {
          console.error("Error fetching child items:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchDirectoryData();
      fetchChildItems();
    } else {
      setError("No directory ID provided.");
      setDirectoryTitle("Error");
      setLoading(false);
    }
  }, [directoryId, getChatbotById, user?.email, user?.role]);

  const handleItemClick = (item) => {
    // Navigate to either a chat page, a component route, or another directory
    if (item.directory) {
      navigate(`/directory?id=${item._id}`);
      return;
    }
    if (item.component) {
      // Mirror the slug logic used in Layout
      let slug = item.component
        .replace(/([A-Z])/g, "-$1")
        .replace(/^-/, "")
        .toLowerCase();
      if (slug.startsWith("/")) slug = slug.substring(1);
      navigate(`/${slug}`);
      return;
    }
    navigate(`/chat?id=${item._id}`);
  };

  // Map provider names to color classes
  const getProviderColorClass = (model) => {
    if (!model) return "";

    const modelLower = model.toLowerCase();
    if (modelLower.includes("gpt") || modelLower.includes("openai")) {
      return "directory-tag-openai";
    } else if (modelLower.includes("gemini") || modelLower.includes("google")) {
      return "directory-tag-google";
    } else if (
      modelLower.includes("claude") ||
      modelLower.includes("anthropic")
    ) {
      return "directory-tag-anthropic";
    }
    return "directory-tag-other"; // Default for other models
  };

  return (
    <Layout>
      <div className="directory-page-wrapper">
        <div className="directory-page-container">
          {error ? (
          <div className="directory-error">{error}</div>
        ) : (
          <>
            <div className="directory-header">
              <h1>{directoryTitle}</h1>
              {directoryData && directoryData.description && (
                <p className="directory-description">
                  {directoryData.description}
                </p>
              )}
            </div>

            {loading || isCheckingAuth ? (
              <div className="directory-loading">Loading items...</div>
            ) : (
              <div className="directory-chatbot-cards-container">
                {childItems.length > 0 ? (
                  childItems.map((item) => (
                    <div
                      key={item._id}
                      className="directory-chatbot-card"
                      data-is-directory={item.directory}
                      onClick={() => handleItemClick(item)}
                    >
                      <div className="directory-chatbot-card-header">
                        <div className="directory-chatbot-icon">
                          {item.icon ? (
                            isSvgContent(item.icon) ? (
                              <div
                                className="directory-svg-container"
                                dangerouslySetInnerHTML={{
                                  __html: item.icon,
                                }}
                              />
                            ) : (
                              <img
                                src={item.icon}
                                alt={item.title || item.name || "Item"}
                              />
                            )
                          ) : (
                            <div className="directory-default-icon">
                              {(item.title || item.name || "?")
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                          )}
                        </div>

                        <div className="directory-chatbot-tags">
                          {item.tags &&
                            item.tags.length > 0 &&
                            item.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="directory-chatbot-tag"
                                style={{ backgroundColor: getTagColor(tag) }}
                              >
                                {tag}
                              </span>
                            ))}
                          {item.directory && (
                            <span className="directory-chatbot-tag directory-tag-directory">
                              Directory
                            </span>
                          )}
                          {!item.directory && item.model && (
                            <span
                              className={`directory-chatbot-tag ${getProviderColorClass(
                                item.model
                              )}`}
                            >
                              {item.model}
                            </span>
                          )}
                          {!item.directory && item.provider && (
                            <span className="directory-chatbot-tag directory-tag-provider">
                              {item.provider}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="directory-chatbot-content">
                        <h3>{item.title || item.name || "Untitled Item"}</h3>
                        {item.description && <p>{item.description}</p>}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="directory-no-chatbots-message">
                    No items found in this directory.
                  </div>
                )}
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </Layout>
  );
};

export default DirectoryPage;
